import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, Server, FileText, ArrowRight, ShoppingBag, AlertTriangle, Zap, Info,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";
import ProductCard from "./ProductCard";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const needsList = [
  "Catálogo de productos (nombres, precios y fotos de cada producto)",
  "Logo y manual de marca (si lo tienes)",
  "Datos de tu pasarela de pago (cuenta Stripe / Redsys / banco)",
  "Política de envíos y devoluciones",
  "Referencias visuales (tiendas que te gusten)",
  "Dominio deseado (lo registramos nosotros el primer año)",
];

const steps = [
  { n: 1, title: "Contratas el servicio", desc: "Pago único de 3.000€ (o 3 plazos con Klarna)." },
  { n: 2, title: "Nos das tu catálogo y material", desc: "Productos, fotos, info y referencias." },
  { n: 3, title: "Montamos tu tienda", desc: "Diseño, productos, fichas, pasarela y configuración." },
  { n: 4, title: "Conectamos tu catálogo", desc: "Vinculación con Google Merchant Center y Meta." },
  { n: 5, title: "Revisas y ajustamos", desc: "Hasta 2 rondas de revisión." },
  { n: 6, title: "Publicamos", desc: "Tu tienda online lista para vender." },
];

const TiendaOnlineDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      {/* HERO */}
      <ServiceHero
        eyebrow="Servicio puntual · Pago único"
        title={<>Tienda <Highlight>Online</Highlight></>}
        description={<>Tu negocio vendiendo online. Una tienda completa, profesional y lista para empezar a facturar desde el primer día.</>}
        includes={["3.000€ (IVA incl.) · Pago único", "Financiable en 3 plazos con Klarna", "Entrega en 30-45 días"]}
        primaryCta={{ label: "Solicitar mi tienda", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
      />

      {/* INCLUYE / NO INCLUYE — JUSTO DEBAJO DEL HERO */}
      <div id="incluye">
        <IncludesExcludes
          includes={[
            "Tienda online completa adaptada a tu identidad visual",
            "<strong>Productos sin límite</strong> (carga completa de tu catálogo)",
            "Redacción de fichas de producto incluida",
            "<strong>Vinculación de tu catálogo con Google Merchant Center y Meta</strong>",
            "Pasarela de pago (Stripe, Redsys o PayPal)",
            "Configuración de envíos y zonas",
            "Hosting + dominio incluidos el primer año (después 149€/año)",
            "Páginas legales (aviso legal, privacidad, cookies, devoluciones)",
            "Configuración SEO básica de producto",
            "Analytics 4 + eventos de e-commerce",
            "Maquetación responsive",
            "Hasta 2 rondas de revisión",
          ]}
          excludes={[
            "Configuración de impuestos (lo gestiona tu gestoría o asesor fiscal)",
            "Feed de TikTok",
            "Multilingüe (la tienda se entrega en 1 solo idioma)",
            "Fotografía profesional de producto",
            "Integraciones con ERP o sistemas de gestión externos",
            "Venta en marketplaces (Amazon, etc.)",
            "Comisiones de la pasarela de pago (las cobra la pasarela)",
            "Mantenimiento de cambios posteriores",
          ]}
        />
      </div>

      {/* AVISO IMPUESTOS */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto flex items-start gap-3 p-5 rounded-2xl bg-destructive/5 border-2 border-destructive/30">
            <AlertTriangle size={22} className="text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-destructive mb-1">Importante: la configuración de impuestos NO está incluida</div>
              <p className="text-sm text-muted-foreground">La gestionará tu gestoría o asesor fiscal según el régimen fiscal de tu negocio. La tienda se entrega además en <strong className="text-foreground">1 solo idioma</strong> y sin feed de TikTok.</p>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* CATÁLOGO CONECTADO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Zap className="w-7 h-7" />
              </div>
              <div>
                <Badge variant="secondary" className="mb-3">Tu catálogo, conectado</Badge>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Tu catálogo vinculado a Google y Meta</h3>
                <p className="text-muted-foreground">
                  Dejamos tu catálogo de productos conectado con <strong className="text-foreground">Google Merchant Center</strong> y con <strong className="text-foreground">Meta</strong>. Eso significa que tus productos están listos para aparecer en Google Shopping y para usarse en campañas de catálogo en Facebook e Instagram desde el primer día. La base perfecta para vender con publicidad.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* HOSTING */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-card border-2 border-border">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Server className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Hosting y dominio incluidos el primer año</h3>
                <p className="text-muted-foreground">
                  Tu tienda incluye <strong className="text-foreground">hosting y dominio gratis durante el primer año</strong>. A partir del segundo año, la renovación es de solo <strong className="text-foreground">149€/año (IVA incluido)</strong>.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* 3 COLUMNAS */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["Productos ilimitados", "Pasarela + envíos", "Catálogo conectado a Google y Meta"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo de entrega", items: ["Entre 30 y 45 días", "Desde la recepción del catálogo completo"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["3.000€ (IVA incluido)", "Pago único o 3 plazos con Klarna", "Renovación: 149€/año (hosting + dominio)"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos de ti</h3>
            <p className="text-muted-foreground">Para arrancar tu proyecto y cumplir el plazo de entrega, necesitamos que nos facilites:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
          <AnimateIn>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20 mt-4">
              <Info size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                El plazo de entrega (<strong className="text-foreground">30-45 días</strong>) empieza a contar cuando recibimos todo el material, incluido el catálogo completo.
              </p>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <AnimateIn key={s.n} delay={i * 0.06}>
              <div className="flex items-start gap-5 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">
                  {s.n}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{s.title}</h4>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* EJEMPLOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Portfolio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Ejemplos de tiendas que hemos hecho</h3>
            <p className="text-muted-foreground">Una muestra de tiendas online que hemos diseñado para nuestros clientes.</p>
          </div>
        </AnimateIn>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <AnimateIn key={idx} delay={idx * 0.06}>
              <Card className="overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all group">
                <div className="bg-muted border-b border-border">
                  <div className="h-7 flex items-center px-3 gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                </div>
                <div className="aspect-video bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center group-hover:scale-[1.02] transition-transform">
                  <ShoppingBag className="w-12 h-12 text-primary/30" />
                </div>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* PRODUCT CARD */}
      <ProductCard
        title="Tienda Online"
        description="E-commerce completo, productos ilimitados, pasarela de pago y catálogo conectado a Google y Meta. Entrega en 30-45 días."
        price="3.000€"
        features={[
          "Tienda completa adaptada a tu marca",
          "Productos sin límite + redacción de fichas",
          "Catálogo conectado a Google Merchant y Meta",
          "Pasarela de pago (Stripe, Redsys o PayPal)",
          "Hosting + dominio incluidos el primer año",
          "Financiable en 3 plazos con Klarna",
        ]}
        stripeUrl="https://buy.stripe.com/bJe5kDbL8gXD7ztbZI0480q"
      />

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para empezar a vender online?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 3.000€, financiable en 3 plazos con Klarna. Hosting y dominio del primer año incluidos.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Solicitar mi tienda
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default TiendaOnlineDemo;
